import React, { useCallback, useState, useEffect, useRef, memo } from "react";
import { useDropzone } from "react-dropzone";
import styles from "./style.module.scss";
import LockIcon from "../../../../../../assets/svg/icons/common/LockIcon";
import FileIcon from "../../../../../../assets/svg/icons/common/FileIcon";
import TrashIcon from "../../../../../../assets/svg/icons/common/Trash";
import FileTypeModal from './FileTypeModal';
import { MAX_FILE_SIZE, ACCEPTED_FILE_TYPES, SUPPORTED_FORMATS_TEXT } from './constants';
import { getFileTypeInfo, formatFileSize, FileIconColorMapping } from './utils';
import FiniCityLogo from '../../../../../../../../images/v2/fincity-logo.png';
import { uploadApplicationFiles, getUploadedFiles, deleteUploadedFile } from "../../../../../../../api";
import useSalesFormStore from "../../../../../../stores/sales-form";
import Button from '../../../../../../components/new-button';
import ConfirmationModal from "../../../../../../components/ConfirmationModal";
import toast from "react-hot-toast";

/**
 * Component for displaying benefit item with checkmark icon
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.title - Benefit title
 * @param {string} props.description - Benefit description
 * @returns {React.ReactElement} BenefitItem component
 */
const BenefitItem = memo(({ title, description }) => (
  <div className={styles.benefitItem}>
    <div className={styles.checkIcon}>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <path d="M12 2.25C10.0716 2.25 8.18657 2.82183 6.58319 3.89317C4.97982 4.96451 3.73013 6.48726 2.99218 8.26884C2.25422 10.0504 2.06114 12.0108 2.43735 13.9021C2.81355 15.7934 3.74215 17.5307 5.10571 18.8943C6.46928 20.2579 8.20656 21.1865 10.0979 21.5627C11.9892 21.9389 13.9496 21.7458 15.7312 21.0078C17.5127 20.2699 19.0355 19.0202 20.1068 17.4168C21.1782 15.8134 21.75 13.9284 21.75 12C21.7473 9.41498 20.7192 6.93661 18.8913 5.10872C17.0634 3.28084 14.585 2.25273 12 2.25ZM16.2806 10.2806L11.0306 15.5306C10.961 15.6004 10.8783 15.6557 10.7872 15.6934C10.6962 15.7312 10.5986 15.7506 10.5 15.7506C10.4014 15.7506 10.3038 15.7312 10.2128 15.6934C10.1218 15.6557 10.039 15.6004 9.96938 15.5306L7.71938 13.2806C7.57865 13.1399 7.49959 12.949 7.49959 12.75C7.49959 12.551 7.57865 12.3601 7.71938 12.2194C7.86011 12.0786 8.05098 11.9996 8.25 11.9996C8.44903 11.9996 8.6399 12.0786 8.78063 12.2194L10.5 13.9397L15.2194 9.21937C15.2891 9.14969 15.3718 9.09442 15.4628 9.0567C15.5539 9.01899 15.6515 8.99958 15.75 8.99958C15.8486 8.99958 15.9461 9.01899 16.0372 9.0567C16.1282 9.09442 16.2109 9.14969 16.2806 9.21937C16.3503 9.28906 16.4056 9.37178 16.4433 9.46283C16.481 9.55387 16.5004 9.65145 16.5004 9.75C16.5004 9.84855 16.481 9.94613 16.4433 10.0372C16.4056 10.1282 16.3503 10.2109 16.2806 10.2806Z" fill="#4C70EF" />
      </svg>
    </div>
    <span className={styles.benefitText}>
      <strong>{title}</strong><br />{description}
    </span>
  </div>
));

/**
 * Component for displaying the finicity verification card
 * 
 * @component
 * @returns {React.ReactElement} VerificationCard component
 */
const VerificationCard = memo(() => (
  <div className={styles.verificationCard}>
    <div className={styles.cardHead}>
      <div className={styles.mastercardBadge}>
        <span>A Mastercard Company</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="28" height="16" viewBox="0 0 28 16" fill="none">
          <path d="M17.7858 1.71041H10.2145V14.2896H17.7858V1.71041Z" fill="#FF5F00" />
          <path d="M10.6953 8C10.6941 6.78853 10.991 5.59269 11.5636 4.50301C12.1362 3.41332 12.9694 2.45836 14.0002 1.7104C12.7237 0.782767 11.1907 0.205884 9.57636 0.0456902C7.96202 -0.114504 6.3315 0.148449 4.87116 0.804506C3.41082 1.46056 2.17959 2.48325 1.31819 3.75567C0.456798 5.02809 0 6.4989 0 8C0 9.5011 0.456798 10.9719 1.31819 12.2443C2.17959 13.5167 3.41082 14.5394 4.87116 15.1955C6.3315 15.8516 7.96202 16.1145 9.57636 15.9543C11.1907 15.7941 12.7237 15.2172 14.0002 14.2896C12.9694 13.5416 12.1362 12.5867 11.5636 11.497C10.9911 10.4073 10.6941 9.21147 10.6953 8Z" fill="#EB001B" />
          <path d="M28 8C28.0001 9.50107 27.5433 10.9719 26.682 12.2443C25.8206 13.5167 24.5894 14.5394 23.1291 15.1955C21.6688 15.8515 20.0383 16.1145 18.424 15.9543C16.8097 15.7941 15.2767 15.2172 14.0002 14.2896C15.0301 13.5409 15.8627 12.5858 16.4352 11.4963C17.0076 10.4067 17.3051 9.21127 17.3051 8C17.3051 6.78872 17.0076 5.59326 16.4352 4.50374C15.8627 3.41423 15.0301 2.45911 14.0002 1.7104C15.2767 0.782766 16.8097 0.20588 18.424 0.0456881C20.0383 -0.114504 21.6688 0.148461 23.1291 0.804525C24.5894 1.46059 25.8206 2.48328 26.682 3.7557C27.5433 5.02812 28.0001 6.49892 28 8Z" fill="#F79E1B" />
          <path d="M27.1745 12.9573V12.6997H27.2868V12.6473H27.0007V12.6997H27.1131V12.9573H27.1745ZM27.7298 12.9573V12.6468H27.6421L27.5413 12.8603L27.4404 12.6468H27.3527V12.9573H27.4146V12.723L27.5092 12.925H27.5734L27.6679 12.7225V12.9573H27.7298Z" fill="#F79E1B" />
        </svg>
      </div>
      <div className={styles.imageContainer}>
        <div className={styles.imageGraphic}>


          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="24" fill="#e4e8f5" />
            <path d="M33 18.75H31.5V18C31.5 17.4033 31.2629 16.831 30.841 16.409C30.419 15.9871 29.8467 15.75 29.25 15.75H15.75C15.1533 15.75 14.581 15.9871 14.159 16.409C13.7371 16.831 13.5 17.4033 13.5 18V27C13.5 27.5967 13.7371 28.169 14.159 28.591C14.581 29.0129 15.1533 29.25 15.75 29.25H26.25V30C26.25 30.5967 26.4871 31.169 26.909 31.591C27.331 32.0129 27.9033 32.25 28.5 32.25H33C33.5967 32.25 34.169 32.0129 34.591 31.591C35.0129 31.169 35.25 30.5967 35.25 30V21C35.25 20.4033 35.0129 19.831 34.591 19.409C34.169 18.9871 33.5967 18.75 33 18.75ZM33.75 30C33.75 30.1989 33.671 30.3897 33.5303 30.5303C33.3897 30.671 33.1989 30.75 33 30.75H28.5C28.3011 30.75 28.1103 30.671 27.9697 30.5303C27.829 30.3897 27.75 30.1989 27.75 30V21C27.75 20.8011 27.829 20.6103 27.9697 20.4697C28.1103 20.329 28.3011 20.25 28.5 20.25H33C33.1989 20.25 33.3897 20.329 33.5303 20.4697C33.671 20.6103 33.75 20.8011 33.75 21V30ZM24.75 31.5C24.75 31.6989 24.671 31.8897 24.5303 32.0303C24.3897 32.171 24.1989 32.25 24 32.25H20.25C20.0511 32.25 19.8603 32.171 19.7197 32.0303C19.579 31.8897 19.5 31.6989 19.5 31.5C19.5 31.3011 19.579 31.1103 19.7197 30.9697C19.8603 30.829 20.0511 30.75 20.25 30.75H24C24.1989 30.75 24.3897 30.829 24.5303 30.9697C24.671 31.1103 24.75 31.3011 24.75 31.5ZM32.25 22.5C32.25 22.6989 32.171 22.8897 32.0303 23.0303C31.8897 23.171 31.6989 23.25 31.5 23.25H30C29.8011 23.25 29.6103 23.171 29.4697 23.0303C29.329 22.8897 29.25 22.6989 29.25 22.5C29.25 22.3011 29.329 22.1103 29.4697 21.9697C29.6103 21.829 29.8011 21.75 30 21.75H31.5C31.6989 21.75 31.8897 21.829 32.0303 21.9697C32.171 22.1103 32.25 22.3011 32.25 22.5Z" fill="#4C70EF" />
          </svg>

          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
            <path d="M7 6L0.25 11.1962L0.25 0.803847L7 6Z" fill="#BBC9CB" />
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
            <path d="M7 6L0.25 11.1962L0.25 0.803847L7 6Z" fill="#BBC9CB" />
          </svg>

          <img
            src={FiniCityLogo}
            alt="Finicity Integration"
            className={styles.fincity}
          />

          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
            <path d="M7 6L0.25 11.1962L0.25 0.803847L7 6Z" fill="#BBC9CB" />
          </svg>
          <svg xmlns="http://www.w3.org/2000/svg" width="7" height="12" viewBox="0 0 7 12" fill="none">
            <path d="M7 6L0.25 11.1962L0.25 0.803847L7 6Z" fill="#BBC9CB" />
          </svg>


          <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="24" cy="24" r="24" fill="#e4e8f5" />
            <path d="M35.25 31.5C35.25 31.6989 35.171 31.8897 35.0303 32.0303C34.8897 32.171 34.6989 32.25 34.5 32.25H13.5C13.3011 32.25 13.1103 32.171 12.9697 32.0303C12.829 31.8897 12.75 31.6989 12.75 31.5C12.75 31.3011 12.829 31.1103 12.9697 30.9697C13.1103 30.829 13.3011 30.75 13.5 30.75H34.5C34.6989 30.75 34.8897 30.829 35.0303 30.9697C35.171 31.1103 35.25 31.3011 35.25 31.5ZM13.5281 21.2044C13.4835 21.0471 13.4915 20.8795 13.5509 20.7272C13.6103 20.5749 13.7179 20.4462 13.8572 20.3606L23.6072 14.3606C23.7253 14.288 23.8613 14.2495 24 14.2495C24.1387 14.2495 24.2747 14.288 24.3928 14.3606L34.1428 20.3606C34.2822 20.4461 34.3898 20.5747 34.4493 20.7269C34.5088 20.8792 34.517 21.0467 34.4725 21.204C34.428 21.3613 34.3334 21.4998 34.2029 21.5984C34.0725 21.6969 33.9135 21.7501 33.75 21.75H31.5V27.75H33C33.1989 27.75 33.3897 27.829 33.5303 27.9697C33.671 28.1103 33.75 28.3011 33.75 28.5C33.75 28.6989 33.671 28.8897 33.5303 29.0303C33.3897 29.171 33.1989 29.25 33 29.25H15C14.8011 29.25 14.6103 29.171 14.4697 29.0303C14.329 28.8897 14.25 28.6989 14.25 28.5C14.25 28.3011 14.329 28.1103 14.4697 27.9697C14.6103 27.829 14.8011 27.75 15 27.75H16.5V21.75H14.25C14.0867 21.75 13.9278 21.6968 13.7975 21.5983C13.6672 21.4998 13.5726 21.3615 13.5281 21.2044ZM25.5 27C25.5 27.1989 25.579 27.3897 25.7197 27.5303C25.8603 27.671 26.0511 27.75 26.25 27.75C26.4489 27.75 26.6397 27.671 26.7803 27.5303C26.921 27.3897 27 27.1989 27 27V22.5C27 22.3011 26.921 22.1103 26.7803 21.9697C26.6397 21.829 26.4489 21.75 26.25 21.75C26.0511 21.75 25.8603 21.829 25.7197 21.9697C25.579 22.1103 25.5 22.3011 25.5 22.5V27ZM21 27C21 27.1989 21.079 27.3897 21.2197 27.5303C21.3603 27.671 21.5511 27.75 21.75 27.75C21.9489 27.75 22.1397 27.671 22.2803 27.5303C22.421 27.3897 22.5 27.1989 22.5 27V22.5C22.5 22.3011 22.421 22.1103 22.2803 21.9697C22.1397 21.829 21.9489 21.75 21.75 21.75C21.5511 21.75 21.3603 21.829 21.2197 21.9697C21.079 22.1103 21 22.3011 21 22.5V27Z" fill="#4C70EF" />
          </svg>


        </div>
      </div>
      <h3 className={styles.verificationTitle}>
        Speed Up Your Application with Secure Instant Verification
      </h3>
      <div className={styles.benefitsList}>
        <BenefitItem
          title="Save time:"
          description="Reduces document requirements."
        />
        <BenefitItem
          title="Bank-level security:"
          description="Your data is encrypted, we will never have access to your credentials."
        />
        <BenefitItem
          title="Better offers:"
          description="Unlocks better rates, faster setup, and funding offers."
        />
      </div>
    </div>
    <button className={styles.fastTrackButton}>
      <svg xmlns="http://www.w3.org/2000/svg" width="25" height="24" viewBox="0 0 25 24" fill="none">
        <path d="M20.5491 11.7617L10.0491 23.0117C9.93784 23.1305 9.79097 23.2098 9.63065 23.2377C9.47034 23.2657 9.30528 23.2407 9.16039 23.1667C9.0155 23.0926 8.89863 22.9734 8.82743 22.827C8.75623 22.6807 8.73455 22.5152 8.76568 22.3555L10.14 15.4808L4.73724 13.452C4.62121 13.4086 4.51773 13.3372 4.43606 13.244C4.35439 13.1508 4.29706 13.0389 4.26921 12.9182C4.24135 12.7975 4.24383 12.6718 4.27642 12.5522C4.30902 12.4327 4.37071 12.3231 4.45599 12.2333L14.956 0.983274C15.0673 0.864526 15.2141 0.785191 15.3744 0.75724C15.5348 0.729289 15.6998 0.75424 15.8447 0.828326C15.9896 0.902412 16.1065 1.02161 16.1777 1.16794C16.2489 1.31428 16.2705 1.47979 16.2394 1.63952L14.8613 8.52171L20.2641 10.5476C20.3793 10.5913 20.4819 10.6627 20.563 10.7555C20.644 10.8483 20.7009 10.9596 20.7287 11.0796C20.7565 11.1996 20.7544 11.3246 20.7224 11.4436C20.6904 11.5626 20.6296 11.6718 20.5454 11.7617H20.5491Z" fill="white" />
      </svg>
      Fast-Track My Approval
    </button>
  </div>
));

/**
 * Component for displaying a divider with text
 * 
 * @component
 * @param {Object} props - Component props
 * @param {string} props.text - Text to display in the divider
 * @returns {React.ReactElement} TextDivider component
 */
const TextDivider = memo(({ text }) => (
  <div className={styles.divider}>
    <div className={styles.dividerLine}></div>
    <span className={styles.dividerText}>{text}</span>
    <div className={styles.dividerLine}></div>
  </div>
));

/**
 * Component for displaying a file item in the list of uploaded files
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.file - File object
 * @param {number} props.index - File index
 * @param {Function} props.onRemove - Function to call when removing the file
 * @returns {React.ReactElement} FileItem component
 */
const FileItem = memo(({ file, index, onRemove }) => {
  const truncatedName = file.file.length > 25
    ? file.file.substring(0, 22) + '...' + file.file.split('.').pop()
    : file.file;

  const extension = file.file.split('.').pop();

  return (
    <div className={styles.fileItem}>
      <div className={styles.fileInfo}>
        <div className={styles.fileIcon}>
          <FileIcon size={16} color={FileIconColorMapping[extension]} />
        </div>
        <div className={styles.fileDetails}>
          <span className={styles.fileName} title={file.file}>
            {truncatedName}
          </span>
        </div>
      </div>

      <Button
        variant="ghost"
        icon={<TrashIcon color="#A4A3A8" />}
        onClick={() => onRemove(file)}
        type="button"
        size="small"
      />
    </div>
  );
});

/**
 * Component for uploading files with drag and drop functionality
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Object} props.formik - Formik instance for form handling
 * @returns {React.ReactElement} FileUpload component
 */
const FileUpload = ({ formik }) => {
  const values = formik?.values || {};
  const setFieldValue = formik?.setFieldValue || (() => { });
  const [fileError, setFileError] = useState(null);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const isMounted = useRef(true);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [isDeleteFileConfirmationModalOpen, setIsDeleteFileConfirmationModalOpen] = useState(false)

  // store data
  const applicationData = useSalesFormStore((state) => state.applicationData);

  const getFilesData = async () => {
    if(applicationData?.id){
      const response = await getUploadedFiles({ id: applicationData?.id });
      if (response.status) {
        setUploadedFiles(response.uploads);
      }
    }
  }

  // Cleanup on unmount
  useEffect(() => {
    getFilesData();

    return () => {
      isMounted.current = false;

      // Cleanup file previews when component unmounts
      if (Array.isArray(values.files)) {
        values.files.forEach(file => {
          if (file.preview) {
            URL.revokeObjectURL(file.preview);
          }
        });
      }

      // Clean up pending files
      pendingFiles.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });

      setShowTypeModal(false);
      setPendingFiles([]);
    };
  }, []);

  // Initialize files array if needed
  useEffect(() => {
    if (!Array.isArray(values.files) && isMounted.current) {
      setFieldValue('files', []);
    }
  }, [values.files, setFieldValue]);

  /**
   * Handle file drop
   * @param {File[]} acceptedFiles - Files accepted by dropzone
   * @param {Object[]} rejectedFiles - Files rejected by dropzone
   */
  const onDrop = useCallback((acceptedFiles = [], rejectedFiles = []) => {
    if (!isMounted.current) return;

    if (!Array.isArray(acceptedFiles)) {
      acceptedFiles = [];
    }

    if (rejectedFiles && rejectedFiles.length > 0) {
      const errorMessages = rejectedFiles.map(file => {
        if (file.file.size > MAX_FILE_SIZE) {
          return `${file.file.name} exceeds the 10MB size limit.`;
        }
        return `${file.file.name} has an unsupported file type.`;
      });
      setFileError(errorMessages.join(' '));
      return;
    }

    setFileError(null);

    const newFiles = acceptedFiles.map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      preview: URL.createObjectURL(file),
      uploaded: new Date().toISOString()
    }));

    setPendingFiles(newFiles);
    setShowTypeModal(true);
  }, [isMounted]);

  /**
   * Handle file type selection submission
   * @param {Object} fileTypes - Map of file names to selected document types
   */
  const handleFileTypeSubmit = async (fileTypes) => {
    const filesWithTypes = pendingFiles.map(file => ({
      ...file,
      documentType: fileTypes[file.name]
    }));

    const currentFiles = Array.isArray(values.files) ? values.files : [];
    const files = [...currentFiles, ...filesWithTypes];
    const formData = new FormData();

    files.forEach(file => {
      formData.append('files[]', file.file);
    });

    formData.append('fileTypes', JSON.stringify(fileTypes));
    formData.append('editslug', applicationData.editslug);
    const response = await uploadApplicationFiles(formData);

    if (response.status) {
      getFilesData();
      setShowTypeModal(false);
      setPendingFiles([]);
      toast.success(`File uploaded successfully`);
    }
  };

  /**
   * Remove a file from the files array
   * @param {number} index - Index of the file to remove
   */
  const removeFile = async (file) => {
    setFileToDelete(file);
    setIsDeleteFileConfirmationModalOpen(true);   
  };

  // Configure dropzone
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: MAX_FILE_SIZE,
    accept: ACCEPTED_FILE_TYPES,
    multiple: true,
    noClick: false,
    noKeyboard: false,
    preventDropOnDocument: true
  });

  const confirmDeleteFile = async () => {
    const response = await deleteUploadedFile({ id: fileToDelete.id });
    if (response.status) {
      getFilesData();
      toast.success(`File ${fileToDelete.file} deleted successfully`);
      setIsDeleteFileConfirmationModalOpen(false);
    }
  }

  return (
    <div className={styles.container}>
      <VerificationCard />

      <TextDivider text="PREFER NOT TO?" />

      {/* File Upload Section */}
      <div className={styles.header}>
        <h2 className={styles.title}>Upload documents instead</h2>
      </div>

      <div {...getRootProps()} className={`${styles.dropzone} ${isDragActive ? styles.active : ''}`}>
        <input {...getInputProps()} />
        <div className={styles.dropzoneContent}>
          <div className={styles.textContent}>
            <p className={styles.dropzoneText}>
              Upload files by drag and drop or
              <br />
              <span className={styles.clickText}>click to upload</span>
            </p>
            <p className={styles.fileTypes}>{SUPPORTED_FORMATS_TEXT}</p>
          </div>
          <div className={styles.encryptionMessage}>
            <LockIcon color="#A4A3A8" />
            <span>256-bit encryption</span>
          </div>
        </div>
      </div>

      {fileError && (
        <div className={styles.errorMessage} role="alert">
          {fileError}
        </div>
      )}

      {Array.isArray(uploadedFiles) && uploadedFiles.length > 0 && (
        <div className={styles.filesList}>
          {uploadedFiles.map((file, index) => (
            <FileItem
              key={index}
              file={file}
              index={index}
              onRemove={removeFile}
            />
          ))}
        </div>
      )}

      {showTypeModal && (
        <FileTypeModal
          isOpen={showTypeModal}
          onClose={() => setShowTypeModal(false)}
          files={pendingFiles}
          onSubmit={handleFileTypeSubmit}
        />
      )}

      {fileToDelete && isDeleteFileConfirmationModalOpen && (
        <ConfirmationModal
          open={isDeleteFileConfirmationModalOpen}
          onClose={() => setIsDeleteFileConfirmationModalOpen(false)}
          onConfirmClick={() => confirmDeleteFile()}
          message={`Are you sure you want to delete this file ${fileToDelete?.file} ?`}
        />
      )}

    </div>
  );
};

export default FileUpload; 